/**
 * session.ts — the opaque session token store.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: login/register return an OPAQUE session token (not a decodable JWT — the
 * gateway looks it up server-side). The SPA stores it in localStorage and sends
 * it as `Authorization: Bearer <token>` on every authed call. A 401 on an authed
 * call clears the token and notifies subscribers (the AuthProvider routes to
 * /auth). All storage access is guarded so private-mode / SSR never crashes.
 *
 * WHICH STORE (Clinics-in-Core, 2026-09-07). Standalone: localStorage — a session
 * survives a closed tab, the behaviour every patient has had since launch.
 * Core-hosted: sessionStorage — that build shares an ORIGIN with a GPS Core
 * portal, and a Clinics bearer parked in the origin's localStorage would be
 * readable by any script Core ever ships there and would outlive the tab.
 * Tab-scoped storage bounds both; the Core handoff (/auth-core) simply re-mints
 * on the next visit. Chosen at call time from the build's host mode (config.ts).
 */
import { isCoreHosted } from '../config';

const STORAGE_KEY = 'gps.session';

/** The token store for THIS build — see the module doc. */
function store(): Storage {
  return isCoreHosted() ? sessionStorage : localStorage;
}

/** Subscribers notified when the session is cleared by a 401 (or logout). */
const unauthorizedListeners = new Set<() => void>();

/** Read the stored session token, or null if signed out. */
export function getToken(): string | null {
  try {
    return store().getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist a new session token. */
export function setToken(token: string): void {
  try {
    store().setItem(STORAGE_KEY, token);
  } catch {
    // Best-effort — an app in private mode simply won't persist the session.
  }
}

/** Remove the stored session token. */
export function clearToken(): void {
  try {
    store().removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Subscribe to unauthorized (401/logout) events; returns an unsubscribe fn. */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/** Clear the session and notify subscribers (called by the client on a 401). */
export function notifyUnauthorized(): void {
  clearToken();
  for (const listener of unauthorizedListeners) listener();
}
